// Fix AU (2026-09-06): convocatoria del día siguiente. Reemplaza la planilla de
// Excel que Edson arma a mano todas las tardes: quién va a qué obra, con qué
// residente, qué tarea y en qué nivel. La cantidad por obra se cuenta sola; lo
// único manual es el equipo tercerizado. Al final, el personal activo que quedó
// sin asignar. El botón de imagen genera un PNG para mandar al grupo de WhatsApp.
import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import { X, Loader2, Plus, Users, Image as ImageIcon, Save, Copy, Trash2 } from "lucide-react";
import axios from "axios";
import { API } from "../../contexts/AuthContext";
import { toast } from "sonner";

const opts = { withCredentials: true };
let __filaSeq = 0;
const nuevaFila = (base = {}) => ({
  __uid: `cv-${Date.now()}-${++__filaSeq}`,
  funcionario_id: "", funcionario_nombre: "", obra_id: "", obra_nombre: "",
  residente_nombre: "", tarea_nombre: "", nivel_label: "", trabajo: "", ...base,
});

// Paleta suave por obra, para que los bloques se distingan en la imagen.
const COLORES = ["#DCFCE7", "#DBEAFE", "#FEF9C3", "#FCE7F3", "#E0E7FF", "#FFEDD5", "#CCFBF1"];

export function ConvocatoriaModal({ open, onClose }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [fecha, setFecha] = useState("");
  const [filas, setFilas] = useState([]);
  const [obrasExtra, setObrasExtra] = useState([]);   // { obra_id, obra_nombre, tercerizados }
  const [catalogos, setCatalogos] = useState({ personal: [], residentes: [], obras: [], tareas: [] });
  const [nivelesPorObra, setNivelesPorObra] = useState({});
  const [personalLibre, setPersonalLibre] = useState([]);
  const [obrasParaImagen, setObrasParaImagen] = useState([]);
  const [meta, setMeta] = useState({ actualizado_por: "", updated_at: "" });
  const canvasRef = useRef(null);

  const cargar = useCallback(async (f) => {
    setLoading(true);
    try {
      const { data: cat } = await axios.get(`${API}/convocatorias/catalogos`, opts);
      setCatalogos(cat);
      const dia = f || cat.manana;
      setFecha(dia);
      const { data } = await axios.get(`${API}/convocatorias`, { ...opts, params: { fecha: dia } });
      setFilas((data.filas || []).map(x => nuevaFila(x)));
      setObrasExtra(data.obras || []);
      setPersonalLibre(data.personal_libre || []);
      setMeta({ actualizado_por: data.actualizado_por, updated_at: data.updated_at });
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo cargar la convocatoria");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { if (open) cargar(null); }, [open, cargar]);

  const cambiarFecha = async (nueva) => { setFecha(nueva); await cargar(nueva); };

  const pedirNiveles = useCallback(async (obraId) => {
    if (!obraId || nivelesPorObra[obraId]) return;
    try {
      const { data } = await axios.get(`${API}/convocatorias/niveles/${obraId}`, opts);
      setNivelesPorObra(p => ({ ...p, [obraId]: data.niveles || [] }));
    } catch { setNivelesPorObra(p => ({ ...p, [obraId]: [] })); }
  }, [nivelesPorObra]);

  const setFila = (uid, campo, valor) => setFilas(p => p.map(f => f.__uid === uid ? { ...f, [campo]: valor } : f));

  const elegirFuncionario = (uid, id) => {
    const w = catalogos.personal.find(p => p.id === id);
    setFilas(p => p.map(f => f.__uid === uid ? { ...f, funcionario_id: id, funcionario_nombre: w?.nombre || "" } : f));
  };

  const elegirObra = (uid, valor) => {
    // valor = id de obra del catálogo, o texto libre si el usuario escribió una
    const o = catalogos.obras.find(x => x.id === valor);
    setFilas(p => p.map(f => f.__uid === uid
      ? { ...f, obra_id: o ? o.id : "", obra_nombre: o ? o.nombre : valor, nivel_label: "" }
      : f));
    if (o) pedirNiveles(o.id);
  };

  // Agrupado por obra, en el orden en que aparecen las filas.
  const grupos = useMemo(() => {
    const m = new Map();
    filas.forEach(f => {
      const clave = f.obra_nombre || "(sin obra)";
      if (!m.has(clave)) m.set(clave, { obra_nombre: clave, obra_id: f.obra_id || "", filas: [] });
      m.get(clave).filas.push(f);
    });
    return [...m.values()].map(g => {
      const extra = obrasExtra.find(o => o.obra_nombre === g.obra_nombre) || {};
      const terc = Number(extra.tercerizados) || 0;
      return { ...g, tercerizados: terc, cantidad: g.filas.length + terc };
    });
  }, [filas, obrasExtra]);

  const setTercerizados = (obra_nombre, obra_id, valor) => {
    setObrasExtra(p => {
      const otras = p.filter(o => o.obra_nombre !== obra_nombre);
      return [...otras, { obra_id, obra_nombre, tercerizados: Number(valor) || 0 }];
    });
  };

  const guardar = async () => {
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/convocatorias`, {
        fecha,
        filas: filas.filter(f => (f.funcionario_nombre || "").trim() || (f.obra_nombre || "").trim())
          .map(({ __uid, ...rest }) => rest),
        obras: obrasExtra,
      }, opts);
      setPersonalLibre(data.personal_libre || []);
      setMeta({ actualizado_por: data.actualizado_por, updated_at: data.updated_at });
      toast.success("Convocatoria guardada");
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo guardar");
    } finally { setSaving(false); }
  };

  const copiarDeAyer = async () => {
    try {
      const d = new Date(fecha + "T12:00:00");
      d.setDate(d.getDate() - 1);
      const desde = d.toISOString().slice(0, 10);
      const { data } = await axios.get(`${API}/convocatorias/copiar`, { ...opts, params: { desde } });
      if (!(data.filas || []).length) { toast.info(`No hay convocatoria del ${desde}`); return; }
      setFilas((data.filas || []).map(x => nuevaFila(x)));
      setObrasExtra(data.obras || []);
      toast.success(`Copiada la convocatoria del ${desde} — revisá y guardá`);
    } catch { toast.error("No se pudo copiar"); }
  };

  // ─── Imagen para WhatsApp (canvas, sin librerías) ───────────────────────
  const generarImagen = () => {
    const elegidas = grupos.filter(g => obrasParaImagen.includes(g.obra_nombre));
    if (elegidas.length === 0) { toast.error("Elegí al menos una obra para la imagen"); return; }
    const ANCHO = 1200, PAD = 24, FILA = 34, ENC_OBRA = 44;
    const COLS = [
      { k: "funcionario_nombre", label: "FUNCIONARIO", w: 260 },
      { k: "residente_nombre", label: "RESIDENTE", w: 180 },
      { k: "tarea_nombre", label: "TAREA", w: 260 },
      { k: "nivel_label", label: "NIVEL", w: 120 },
      { k: "trabajo", label: "TRABAJO A REALIZAR", w: 328 },
    ];
    let alto = PAD * 2 + 70;
    elegidas.forEach(g => { alto += ENC_OBRA + 28 + g.filas.length * FILA + 16; });
    const c = canvasRef.current;
    c.width = ANCHO; c.height = alto;
    const ctx = c.getContext("2d");
    ctx.fillStyle = "#FFFFFF"; ctx.fillRect(0, 0, ANCHO, alto);
    ctx.fillStyle = "#1A4B7A"; ctx.font = "bold 26px Arial";
    ctx.fillText("CONVOCATORIA DE OBRA", PAD, PAD + 30);
    ctx.fillStyle = "#374151"; ctx.font = "18px Arial";
    const [a, m, d] = fecha.split("-");
    ctx.fillText(`Fecha: ${d}/${m}/${a}`, PAD, PAD + 58);
    let y = PAD + 88;
    elegidas.forEach((g, gi) => {
      ctx.fillStyle = COLORES[gi % COLORES.length];
      ctx.fillRect(PAD, y, ANCHO - PAD * 2, ENC_OBRA);
      ctx.fillStyle = "#111827"; ctx.font = "bold 20px Arial";
      ctx.fillText(`${g.obra_nombre}  ·  ${g.cantidad} persona${g.cantidad !== 1 ? "s" : ""}${g.tercerizados ? ` (incluye ${g.tercerizados} tercerizado${g.tercerizados !== 1 ? "s" : ""})` : ""}`, PAD + 12, y + 29);
      y += ENC_OBRA;
      ctx.fillStyle = "#6B7280"; ctx.font = "bold 13px Arial";
      let x = PAD + 12;
      COLS.forEach(col => { ctx.fillText(col.label, x, y + 19); x += col.w; });
      y += 28;
      ctx.font = "15px Arial";
      g.filas.forEach((f, fi) => {
        if (fi % 2 === 1) { ctx.fillStyle = "#F9FAFB"; ctx.fillRect(PAD, y, ANCHO - PAD * 2, FILA); }
        ctx.fillStyle = "#111827";
        let cx = PAD + 12;
        COLS.forEach(col => {
          const txt = String(f[col.k] || "—");
          const max = col.w - 14;
          let corto = txt;
          while (ctx.measureText(corto).width > max && corto.length > 3) corto = corto.slice(0, -2);
          if (corto !== txt) corto = corto.slice(0, -1) + "…";
          ctx.fillText(corto, cx, y + 22);
          cx += col.w;
        });
        y += FILA;
      });
      y += 16;
    });
    c.toBlob((blob) => {
      const url = URL.createObjectURL(blob);
      const a2 = document.createElement("a");
      a2.href = url; a2.download = `Convocatoria_${fecha}.png`;
      document.body.appendChild(a2); a2.click(); a2.remove();
      setTimeout(() => URL.revokeObjectURL(url), 30000);
      toast.success("Imagen generada — ya se puede enviar por WhatsApp");
    }, "image/png");
  };

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-[9990] flex items-center justify-center p-3" data-testid="convocatoria-modal">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-6xl max-h-[92vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0">
          <div>
            <h3 className="text-base font-bold text-gray-900 flex items-center gap-2">
              <Users size={17} className="text-[#EA580C]" /> Convocatoria
            </h3>
            <p className="text-xs text-gray-500 mt-0.5">
              Lista de convocados por obra. {meta.updated_at ? `Última edición: ${meta.actualizado_por}` : "Todavía sin guardar."}
            </p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100" data-testid="convocatoria-close"><X size={16} /></button>
        </div>

        <div className="px-6 py-3 border-b border-gray-100 flex items-center gap-3 flex-wrap flex-shrink-0">
          <label className="text-xs font-semibold text-gray-700">Fecha</label>
          <input type="date" value={fecha} onChange={e => cambiarFecha(e.target.value)}
            className="border border-gray-300 rounded-lg px-2 py-1.5 text-xs" data-testid="convocatoria-fecha" />
          <button onClick={copiarDeAyer} className="flex items-center gap-1.5 text-xs font-semibold text-gray-600 border border-gray-200 rounded-lg px-3 py-1.5 hover:bg-gray-50" data-testid="convocatoria-copiar">
            <Copy size={12} /> Copiar la del día anterior
          </button>
          <button onClick={() => setFilas(p => [...p, nuevaFila()])} className="flex items-center gap-1.5 text-xs font-semibold text-[#2E6DA4] border border-[#2E6DA4]/30 rounded-lg px-3 py-1.5 hover:bg-blue-50" data-testid="convocatoria-agregar-fila">
            <Plus size={12} /> Agregar persona
          </button>
          <span className="ml-auto text-xs text-gray-500">{filas.length} convocado(s) · {grupos.length} obra(s)</span>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          {loading ? (
            <div className="flex justify-center py-12"><Loader2 className="animate-spin text-gray-400" size={26} /></div>
          ) : (
            <>
              <div className="overflow-x-auto rounded-xl border border-gray-200">
                <table className="w-full text-xs min-w-[1000px]">
                  <thead className="bg-[#EBF3FB]">
                    <tr>
                      {["Funcionario", "Obra", "Residente a cargo", "Tarea", "Nivel", "Trabajo a realizar", ""].map(h => (
                        <th key={h} className="text-left px-2 py-2 font-bold text-[#1A4B7A] uppercase tracking-wide">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filas.map(f => (
                      <tr key={f.__uid} className="border-t border-gray-100" data-testid={`convocatoria-fila-${f.__uid}`}>
                        <td className="px-2 py-1.5">
                          <select value={f.funcionario_id} onChange={e => elegirFuncionario(f.__uid, e.target.value)}
                            className="w-full border border-gray-200 rounded-lg px-1 py-1 text-[11px] bg-white">
                            <option value="">— Elegir —</option>
                            {catalogos.personal.map(p => <option key={p.id} value={p.id}>{p.nombre}{p.cargo ? ` · ${p.cargo}` : ""}</option>)}
                          </select>
                        </td>
                        <td className="px-2 py-1.5">
                          <select value={f.obra_id || "__manual__"} onChange={e => elegirObra(f.__uid, e.target.value === "__manual__" ? "" : e.target.value)}
                            className="w-full border border-gray-200 rounded-lg px-1 py-1 text-[11px] bg-white">
                            <option value="__manual__">— Escribir a mano —</option>
                            {catalogos.obras.map(o => <option key={o.id} value={o.id}>{o.nombre}</option>)}
                          </select>
                          {!f.obra_id && (
                            <input value={f.obra_nombre} onChange={e => setFila(f.__uid, "obra_nombre", e.target.value)}
                              placeholder="Nombre de la obra" className="mt-1 w-full border border-gray-200 rounded-lg px-1.5 py-1 text-[11px]" />
                          )}
                        </td>
                        <td className="px-2 py-1.5">
                          <input list="convocatoria-residentes" value={f.residente_nombre}
                            onChange={e => setFila(f.__uid, "residente_nombre", e.target.value)}
                            placeholder="Residente" className="w-full border border-gray-200 rounded-lg px-1.5 py-1 text-[11px]" />
                        </td>
                        <td className="px-2 py-1.5">
                          <input list="convocatoria-tareas" value={f.tarea_nombre}
                            onChange={e => setFila(f.__uid, "tarea_nombre", e.target.value)}
                            placeholder="Tarea" className="w-full border border-gray-200 rounded-lg px-1.5 py-1 text-[11px]" />
                        </td>
                        <td className="px-2 py-1.5">
                          <input list={`convocatoria-niveles-${f.obra_id || "manual"}`} value={f.nivel_label}
                            onChange={e => setFila(f.__uid, "nivel_label", e.target.value)}
                            placeholder="Nivel" className="w-full border border-gray-200 rounded-lg px-1.5 py-1 text-[11px]" />
                          {f.obra_id && (
                            <datalist id={`convocatoria-niveles-${f.obra_id}`}>
                              {(nivelesPorObra[f.obra_id] || []).map(n => <option key={n.id} value={n.label} />)}
                            </datalist>
                          )}
                        </td>
                        <td className="px-2 py-1.5">
                          <input value={f.trabajo} onChange={e => setFila(f.__uid, "trabajo", e.target.value)}
                            placeholder="Opcional" className="w-full border border-gray-200 rounded-lg px-1.5 py-1 text-[11px]" />
                        </td>
                        <td className="px-2 py-1.5 text-center">
                          <button onClick={() => setFilas(p => p.filter(x => x.__uid !== f.__uid))} className="text-red-400 hover:text-red-600"><Trash2 size={13} /></button>
                        </td>
                      </tr>
                    ))}
                    {filas.length === 0 && (
                      <tr><td colSpan={7} className="px-3 py-6 text-center text-gray-400">Sin convocados todavía. Agregá personas o copiá la convocatoria del día anterior.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
              <datalist id="convocatoria-residentes">
                {catalogos.residentes.map(r => <option key={r.id} value={r.nombre} />)}
              </datalist>
              <datalist id="convocatoria-tareas">
                {catalogos.tareas.map((t, i) => <option key={i} value={t.nombre} />)}
              </datalist>

              {grupos.length > 0 && (
                <div className="border border-gray-200 rounded-xl p-3">
                  <p className="text-[10px] text-gray-500 uppercase font-semibold tracking-wide mb-2">Resumen por obra · elegí cuáles entran en la imagen</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {grupos.map((g, i) => (
                      <label key={g.obra_nombre} className="flex items-center gap-2 border border-gray-100 rounded-lg px-3 py-2 text-xs" style={{ background: COLORES[i % COLORES.length] + "55" }}>
                        <input type="checkbox" checked={obrasParaImagen.includes(g.obra_nombre)}
                          onChange={e => setObrasParaImagen(p => e.target.checked ? [...p, g.obra_nombre] : p.filter(x => x !== g.obra_nombre))}
                          className="w-3.5 h-3.5 accent-[#EA580C]" data-testid={`convocatoria-imagen-obra-${i}`} />
                        <span className="font-semibold text-gray-800 flex-1">{g.obra_nombre}</span>
                        <span className="text-gray-600">{g.cantidad}</span>
                        <span className="text-[10px] text-gray-500">terc.</span>
                        <input type="number" min="0" value={g.tercerizados}
                          onChange={e => setTercerizados(g.obra_nombre, g.obra_id, e.target.value)}
                          className="w-14 border border-gray-200 rounded px-1 py-0.5 text-[11px] text-center" data-testid={`convocatoria-terc-${i}`} />
                      </label>
                    ))}
                  </div>
                </div>
              )}

              <div className="border border-gray-200 rounded-xl p-3">
                <p className="text-[10px] text-gray-500 uppercase font-semibold tracking-wide mb-2">
                  Personal activo sin asignar ({personalLibre.length})
                </p>
                {personalLibre.length === 0 ? (
                  <p className="text-xs text-gray-400">Todo el personal activo está convocado.</p>
                ) : (
                  <div className="flex flex-wrap gap-1.5" data-testid="convocatoria-personal-libre">
                    {personalLibre.map(p => (
                      <span key={p.id} className="text-[11px] bg-gray-100 text-gray-700 rounded-full px-2 py-0.5">{p.nombre}</span>
                    ))}
                  </div>
                )}
                <p className="text-[10px] text-gray-400 mt-2">Se actualiza al guardar.</p>
              </div>
            </>
          )}
        </div>

        <div className="flex items-center justify-between px-6 py-3 border-t border-gray-100 flex-shrink-0 gap-3 flex-wrap">
          <button onClick={generarImagen} className="flex items-center gap-1.5 border border-[#EA580C] text-[#EA580C] hover:bg-orange-50 px-4 py-2 rounded-lg text-xs font-bold" data-testid="convocatoria-imagen-btn">
            <ImageIcon size={14} /> Generar imagen ({obrasParaImagen.length} obra{obrasParaImagen.length !== 1 ? "s" : ""})
          </button>
          <div className="flex gap-2 ml-auto">
            <button onClick={onClose} className="px-4 py-2 border border-gray-200 rounded-lg text-xs font-semibold text-gray-600">Cerrar</button>
            <button onClick={guardar} disabled={saving}
              className="flex items-center gap-1.5 bg-[#EA580C] hover:bg-[#C2410C] disabled:opacity-50 text-white px-4 py-2 rounded-lg text-xs font-bold" data-testid="convocatoria-guardar">
              {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Guardar
            </button>
          </div>
        </div>
        <canvas ref={canvasRef} className="hidden" />
      </div>
    </div>,
    document.body
  );
}
